import { TeamSettingsLayout } from "../settings/team/team-settings-layout";
import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useFetchPermissionsQuery, useFetchRulesQuery, usePermission } from "@/hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Pencil, Plus } from "lucide-react";
import { FormAddPermissionRule, FormRule } from "@/components/common";
import { isTenantGrant, moduleLabel, permissionGroups, roleLabel } from "@/hooks/rules/permission-ux";

export function Rules() {
  const { checkPermission } = usePermission();
  const rules = useFetchRulesQuery();
  const catalog = useFetchPermissionsQuery();
  const [search, setSearch] = useState("");
  if (!checkPermission("role", "view role")) return <Navigate to="/" replace />;
  const visibleRules = rules.data?.roles.filter(rule => roleLabel(rule.name).toLocaleLowerCase("pt-BR").includes(search.toLocaleLowerCase("pt-BR")));

  return <TeamSettingsLayout current="profiles" actions={<FormRule><Button variant="petrol" disabled={!checkPermission("role", "create role")}><Plus className="mr-2 size-4" />Novo perfil</Button></FormRule>}>
    <section className="space-y-6">
    <h2 className="sr-only">Perfis de acesso</h2>
    <label className="block max-w-sm space-y-2 text-sm text-app-fg">
      <span>Buscar perfil</span><Input value={search} onChange={event => setSearch(event.target.value)} placeholder="Nome do perfil" />
    </label>
    {rules.isError && <div role="alert" className="space-y-3 rounded-xl border border-app-border p-5">
      <p>{rules.data ? "Não foi possível atualizar os perfis de acesso. Os dados anteriores e suas alterações foram mantidos." : "Não foi possível carregar os perfis de acesso."}</p><Button variant="outline" onClick={() => void rules.refetch()}>Tentar novamente</Button>
    </div>}
    {!rules.data && rules.isLoading && <div role="status" aria-label="Carregando perfis" className="space-y-3">{[0, 1, 2].map(key => <Skeleton key={key} className="h-24 w-full rounded-xl" />)}</div>}
    {rules.data && <div className="divide-y divide-app-border rounded-xl border border-app-border bg-app-surface">
      {visibleRules?.length === 0 && <p className="p-6 text-app-fg">Nenhum perfil encontrado.</p>}
      {visibleRules?.map(rule => {
        const groups = permissionGroups(rule.permissions);
        return <article key={rule.id} className="flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="min-w-0 flex-1 space-y-2">
            <h3 className="font-medium text-app-fg-strong">{roleLabel(rule.name)}</h3>
            <p className="text-sm text-app-fg">{rule.permissions.filter(isTenantGrant).length} permissões · {groups.length} módulos</p>
            <p className="text-sm text-app-fg-muted">{groups.slice(0, 4).map(([module]) => moduleLabel(module)).join(" · ") || "Nenhum acesso atribuído"}{groups.length > 4 ? ` · +${groups.length - 4} módulos` : ""}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <FormRule rule={rule}><Button variant="ghost" aria-label={`Renomear ${roleLabel(rule.name)}`} disabled={!checkPermission("role", "update role")}><Pencil className="mr-2 size-4" />Renomear</Button></FormRule>
            <FormAddPermissionRule rule={rule} permissions={catalog.data?.permissions ?? []} isLoading={catalog.isLoading} isError={catalog.isError || rules.isError} onRetry={() => {
              if (rules.isError) void rules.refetch();
              if (catalog.isError) void catalog.refetch();
            }}>
              <Button variant="outline" aria-label={`Editar permissões de ${roleLabel(rule.name)}`} disabled={!checkPermission("permission", "update permission")}>Editar permissões</Button>
            </FormAddPermissionRule>
          </div>
        </article>;
      })}
    </div>}
  </section></TeamSettingsLayout>;
}
